import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type ChangeEventHandler,
  type CompositionEventHandler,
} from 'react';

const SEARCH_DEBOUNCE_MS = 1000;

export interface SearchInputBindings {
  value: string;
  onChange: ChangeEventHandler<HTMLInputElement>;
  onCompositionStart: CompositionEventHandler<HTMLInputElement>;
  onCompositionEnd: CompositionEventHandler<HTMLInputElement>;
}

export interface SearchInputOptions {
  initialValue: string;
  onCommit: (keyword: string) => void;
}

export interface SearchInputController {
  inputProps: SearchInputBindings;
  submit: () => void;
  reset: (value: string) => void;
}

export function useSearchInput({
  initialValue,
  onCommit,
}: SearchInputOptions): SearchInputController {
  const [draft, setDraft] = useState(initialValue);
  const onCommitRef = useRef(onCommit);
  const draftRef = useRef(initialValue);
  const committedRef = useRef(initialValue);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const composingRef = useRef(false);

  useLayoutEffect(() => {
    onCommitRef.current = onCommit;
  }, [onCommit]);

  const cancelTimer = useCallback(() => {
    if (timerRef.current != null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
  }, []);

  const commit = useCallback(
    (force = false) => {
      cancelTimer();
      if (composingRef.current) return;
      const keyword = draftRef.current;
      // 自動検索では、同じ検索語による不要な再取得を発生させない。
      if (!force && keyword === committedRef.current) return;
      committedRef.current = keyword;
      onCommitRef.current(keyword);
    },
    [cancelTimer],
  );

  const submit = useCallback(() => commit(true), [commit]);

  const schedule = useCallback(() => {
    cancelTimer();
    if (composingRef.current) return;
    timerRef.current = setTimeout(() => {
      timerRef.current = null;
      commit();
    }, SEARCH_DEBOUNCE_MS);
  }, [cancelTimer, commit]);

  const onChange: ChangeEventHandler<HTMLInputElement> = event => {
    const value = event.currentTarget.value;
    draftRef.current = value;
    setDraft(value);
    if (!composingRef.current) schedule();
  };

  const onCompositionStart: CompositionEventHandler<HTMLInputElement> = () => {
    composingRef.current = true;
    cancelTimer();
  };

  const onCompositionEnd: CompositionEventHandler<HTMLInputElement> = event => {
    composingRef.current = false;
    const value = event.currentTarget.value;
    draftRef.current = value;
    setDraft(value);
    schedule();
  };

  const reset = useCallback(
    (value: string) => {
      cancelTimer();
      composingRef.current = false;
      draftRef.current = value;
      committedRef.current = value;
      setDraft(value);
    },
    [cancelTimer],
  );

  useEffect(() => () => cancelTimer(), [cancelTimer]);

  return {
    inputProps: {
      value: draft,
      onChange,
      onCompositionStart,
      onCompositionEnd,
    },
    submit,
    reset,
  };
}
